// Angular
import { Injectable } from '@angular/core';

// Libraries
import { OnboardingItem } from '@rosen-group/ngx-onboarding';

// Models
import { AppCleanup } from '../models';
import { OnboardingKey } from '../types/types';
import { OnboardingDefinition } from '../models/onboarding-definition';

@Injectable({
	providedIn: 'root',
})
export class OnboardingSelectorService extends AppCleanup {
	private readonly onboardingDefinitions: {} = {
		'user-profile': [
			{
				selector: '#onboarding',
				headline: 'Onboarding Button',
				details: 'Click here to learn more about the page you are on.',
				textAlign: 'center',
				position: 'top',
			},
		],
		'active-jobs': [
			{
				selector: 'active-jobs',
				headline: 'Active Jobs',
				details: 'When jobs are running, they will be displayed within this area.',
				textAlign: 'center',
				position: 'top',
			},
			{
				selector: '#onbFilterActiveJobs',
				headline: 'Filter',
				details: 'Click here to filter Active Jobs by Service Line.',
				textAlign: 'center',
				position: 'left',
			},
		],
		'job-overview': [
			{
				selector: '#jobOverviewInformation',
				textAlign: 'left',
				position: 'bottom',
				headline: 'Job Information',
				details: `Within this area, the job's company, well, and unit information will be displayed.`,
			},
			{
				selector: '#addComment',
				textAlign: 'center',
				position: 'left',
				headline: 'Add Comment',
				details: 'Click this button to add a comment to the job.',
			},
			{
				selector: '#jobSettings',
				textAlign: 'center',
				position: 'left',
				headline: 'Job Settings',
				details: 'View Job info, modify Job thresholds, and manage Job links.',
			},
			{
				selector: '#onbShowComment',
				textAlign: 'center',
				position: 'left',
				headline: 'Show Comment Log',
			},
			{
				selector: '#onbCommentPin',
				textAlign: 'center',
				position: 'left',
				headline: 'Toggle Comment Log',
			},
			{
				selector: '#onbTimeDuration',
				textAlign: 'center',
				position: 'top',
				headline: 'Chart Interval',
				details: 'Choose the time interval and use the arrows to pan through Chart data. Click and drag to zoom in.',
			},
			{
				selector: '#jobChartDiv',
				textAlign: 'center',
				position: 'top',
				headline: 'Streaming Job Data',
				details: 'When a job is active, its data will stream live within this chart.',
			},
			{
				selector: '#onbToggleChartTooltipsEnabled,#onbToggleChartTooltipsDisabled',
				textAlign: 'center',
				position: 'left',
				headline: 'Toggle Chart Tooltips',
			},
			{
				selector: 'onChartSettingsToggle',
				textAlign: 'center',
				position: 'left',
				headline: 'Edit Chart Settings',
			},
		],
		dashboard: [
			{
				selector: '#onbAddWidgetDashboard',
				headline: 'Add Widget',
				details: 'Add a customizable widget to the Dashboard.',
				textAlign: 'center',
				position: 'left',
			},
			{
				selector: '#onManageDashboard',
				headline: 'Manage Dashboards',
				details: 'Create a new dashboard or modify an existing dashboard here.',
				textAlign: 'center',
				position: 'left',
			},
			{
				selector: '#onbWidgetPanel',
				headline: 'Job Dashboard',
				details: 'Resize and rearrange widgets on your personal dashboards.',
				textAlign: 'center',
				position: 'top',
			},
		],
		'force-monitor': [
			{
				selector: '#onbCommentPin',
				headline: 'Toggle Comment Log',
				textAlign: 'center',
				position: 'left',
			},
		],
		'daily-reports': [
			{
				selector: '#jdrButton',
				headline: 'Job Detail Report',
				details: 'Download or Email the Job Detail Report (JDR).',
				textAlign: 'center',
				position: 'bottom',
			},
			{
				selector: '#dailyInfoSection',
				headline: 'Daily Info',
				details: 'Within this section, users can view a wide variety of manually set job data points. Once set, the Daily Info values are visible within this page and also included in the JDR report.',
				textAlign: 'center',
				position: 'top',
			},
			{
				selector: '#editInfoInfo',
				headline: 'Edit Daily Information',
				details: 'Click here to update the displayed within the Daily Info section, at left.',
				textAlign: 'center',
				position: 'left',
			},
			{
				selector: '#commentEventLogSection',
				headline: 'Comment / Event Log',
				details: 'Comments and related event data associated with the job are displayed within this section.',
				textAlign: 'center',
				position: 'top',
			},
		],
		'raw-data': [
			{
				selector: '#splitMergeCurrentlyProcessing',
				headline: 'Split/Merge Job',
				details: `Click the 'Split/Merge' button to Split the selected job or Merge another job into the selected job. When the job is processing in the background, a processing indicator will display labelled 'Currently Processing'.`,
				textAlign: 'center',
				position: 'left',
			},
			{
				selector: '#onbRawDataJobChart',
				headline: 'Job Chart',
				details: 'To select a certain date range, click and drag the chart.',
				textAlign: 'center',
				position: 'top',
			},
			{
				selector: '#onbRawDataExportButton',
				headline: 'Export',
				details: 'Export your selected data to a CSV file.',
				textAlign: 'center',
				position: 'left',
			},
		],
		'completed-jobs': [
			{
				selector: '#completedJobsTable',
				headline: 'Completed Jobs Table',
				details: 'By default, the most recent 200 completed jobs will display in order of most recently completed.',
				textAlign: 'center',
				position: 'top',
			},
			{
				selector: '#completedJobsSearchButton',
				headline: 'Search',
				details: 'Click the search button to apply a more refined search using a variety of criteria.',
				textAlign: 'center',
				position: 'left',
			},
			{
				selector: '#completedJobsResults',
				headline: 'Results',
				details: 'The number of current matching results out of a maximum of 200.',
				textAlign: 'center',
				position: 'topleft',
			},
		],
	};

	onboardingItems: {} = {};

	constructor() {
		super();

		// Initialize onboarding objects classes.
		Object.keys(this.onboardingDefinitions).forEach((key: OnboardingKey) => {
			this.onboardingItems[key] = this.buildOnboardingItems(key);
		});
	}

	getOnboardingItems(onboardingKey: OnboardingKey): OnboardingItem[] {
		return this.onboardingItems[onboardingKey];
	}

	private buildOnboardingItems(onboardingKey: OnboardingKey): OnboardingItem[] {
		let onboardingItems: OnboardingItem[] = [];

		if (onboardingKey && onboardingKey in this.onboardingDefinitions && this.onboardingDefinitions[onboardingKey]?.length) {
			onboardingItems = this.onboardingDefinitions[onboardingKey].map((definition: OnboardingDefinition) => {
				const { selector, headline, details, textAlign, position } = definition;

				const item: OnboardingItem = new OnboardingItem();
				item.selector = selector;
				item.headline = headline;
				item.details = details;
				item.textAlign = textAlign;
				item.position = position;

				return item;
			});
		}

		return onboardingItems;
	}
}
